import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import { addJournalEntry } from "./starting_balance";
import {
  createNewHierarchy,
  removeHierarchyAccount,
  createNewFundHierarchy,
  removeFundHierarchy,
  normalizeSubAccountId,
} from "./utils";
import { updateHierarchyTx } from "./hierarchyTx";
import { buildFundEditPayload } from "./fundEditPayload";
import { useContext } from "react";
import UserContext from "../../../assets/user_context";
import { useAuth } from "../../../services/use-auth";

// Pure duplicate-number guard shared by the submit flow and unit tests.
// Normalizes both the entered number and every stored number to strings so an
// int-typed legacy accountNumber (e.g. 5000) matches the string entered in the
// form. Returns a reason-carrying failure object on a real duplicate, else null.
export const findDuplicateNumberError = ({
  accounts,
  accountNumber,
  editingAccountId,
}) => {
  const entry = String(accountNumber);
  const taken = (accounts || []).some(
    (account) => String(account.accountNumber) === entry,
  );
  if (taken && !editingAccountId) {
    return {
      error:
        "Account number already exists. Please enter a different account number.",
    };
  }
  return null;
};

/**
 * The three boolean flags on an account, resolved for its type. Always returns
 * real booleans — never undefined, which Firestore rejects outright, failing
 * the whole setDoc rather than just that field.
 *
 * `=== true` rather than `Boolean()` so the writer uses the same predicate as
 * every reader: getReconciliationOverview and getBookkeeperDashboard query
 * `where("isRegister","==",true)`, and runwayMath/enter_bank_transaction and
 * friends test `=== true`. Anything those readers would treat as not-a-register
 * is therefore stored as not-a-register, so a malformed value can only ever
 * settle to the safe default instead of silently inverting.
 *
 * Extracted and pure so this is testable at all — reaching it through
 * useSubmitAccount would mean mocking UserContext, useAuth and Firestore to
 * assert three booleans.
 *
 * Identity fields (accountName, accountType) deliberately get NO such
 * defaulting: coercing those would write bad data instead of failing loudly.
 */
export const buildAccountFlags = ({ accountType, accountEntryData }) => {
  // `?? {}` not a default parameter: defaults do not fire for an explicit null.
  const entry = accountEntryData ?? {};
  const flagged = accountType === "Assets" || accountType === "Liabilities";
  const isRegister = flagged && entry.isRegister === true;
  return {
    isRegister,
    // Illiquid only applies to register accounts; non-registers force false.
    illiquid: isRegister && entry.illiquid === true,
    isPayableReceivable: flagged && entry.isPayableReceivable === true,
  };
};

/**
 * A blank add-account form.
 *
 * This is the UNION of every key controlled by the two add-account surfaces —
 * add_account.jsx and chart_of_accounts/components/InlineAccountForm.jsx — and
 * it has to stay that way. Both forms stay MOUNTED between adds
 * (magange_accounts.js hides one with `display:none`), and the post-create
 * reset REPLACES state rather than merging, so any key missing here becomes
 * `undefined` on the next account.
 *
 * That is not cosmetic. An undefined value flips a MUI input from controlled to
 * uncontrolled: the field keeps displaying the previous account's text while
 * React holds no value, so the label un-floats over stale content and the user
 * cannot tell what will be submitted. On the boolean flags it was worse still —
 * `undefined` reached Firestore, which rejects it and failed the entire write.
 *
 * Adding a field to either form means adding it here.
 */
export const BLANK_ACCOUNT_FORM = Object.freeze({
  type: "",
  accountName: "",
  accountNumber: "",
  group: "",
  isRegister: false,
  illiquid: false,
  isPayableReceivable: false,
  subAccount: false,
  subAccountOf: null,
  dismissStartingBalance: false,
});

//Add account to database
export const useSubmitAccount = ({
  accountEntryData,
  editingAccountId,
  setShowModal,
  setAccountEntryData,
  accountDataToEdit,
  db,
  massAdd,
}) => {
  const { org, user, accounts, funds, setTransactions } =
    useContext(UserContext);
  const handleSubmit = async () => {
    console.log("Adding Account", accountEntryData, "to org", org);
    const accountName = accountEntryData.accountName;
    const accountType = accountEntryData.type;
    const accountNumber = accountEntryData.accountNumber.toString();
    const accountData = {
      accountName: accountName,
      accountType: accountType,
      ...buildAccountFlags({ accountType, accountEntryData }),
      accountNumber: accountNumber,
      disabled: false,
      userMadeChange: user.uid,
      // Include dismissStartingBalance if it's set to true
      ...(accountEntryData.dismissStartingBalance && {
        dismissStartingBalance: true,
      }),
    };
    let accountRef;
    const setAccountDocument = async (accountData, id) => {
      console.log("Setting Account Document", accountData, id);
      const accountRef = doc(db, "orgs", org, "accounts", id);
      console.log("AccountRef:", accountRef);
      await setDoc(accountRef, accountData, { merge: true });
    };

    const createAccount = async (accountNumber) => {
      // Common setup
      accountData.accountNumber = accountNumber.toString();

      // Creating a new account
      if (!editingAccountId) {
        console.log("Adding Account with atomic transaction: ", accountData);

        // Atomic read-modify-write on the FRESH hierarchy: compose the new entry
        // inside the transaction so a concurrent writer's entry can't be lost.
        let newAccountRef;
        await updateHierarchyTx(db, org, ["accountsHierarchy"], (fresh) => {
          const accountsRef = collection(db, "orgs", org, "accounts");
          newAccountRef = doc(accountsRef);

          const newHierarchy = createNewHierarchy(
            fresh.accountsHierarchy,
            accountEntryData.type,
            accountEntryData.group,
            newAccountRef.id,
            accountEntryData.subAccount
              ? accountEntryData.subAccountOf.id
              : null,
            null,
          );

          return {
            writes: { accountsHierarchy: newHierarchy },
            extraWrites: (transaction) =>
              transaction.set(newAccountRef, accountData),
          };
        });

        accountRef = newAccountRef;
        console.log("Added Account with transaction: ", accountRef);
        return accountRef; // Exit createAccount — modal close/reset deferred to handleSubmit
      }

      // Editing an existing account
      if (accountEntryData.type !== accountDataToEdit.accountType) {
        // Handle the scenario where the account type cannot change — surface a
        // reason instead of silently returning undefined (fake failure).
        console.error("Cannot change account type");
        return { error: "An account's type cannot be changed." };
      } else {
        accountRef = editingAccountId;

        // Check if structural properties changed (properties that affect hierarchy position)
        const structuralPropsChanged =
          accountEntryData.group !== accountDataToEdit.group ||
          accountEntryData.subAccount !== accountDataToEdit.subAccount ||
          (accountEntryData.subAccount &&
            accountEntryData.subAccountOf?.id !==
              accountDataToEdit.subAccountOf?.id);

        console.log("Structural properties changed:", structuralPropsChanged);
        console.log("Comparison details:", {
          groupChanged: accountEntryData.group !== accountDataToEdit.group,
          groupValues: {
            new: accountEntryData.group,
            old: accountDataToEdit.group,
          },
          subAccountChanged:
            accountEntryData.subAccount !== accountDataToEdit.subAccount,
          subAccountValues: {
            new: accountEntryData.subAccount,
            old: accountDataToEdit.subAccount,
          },
          subAccountOfChanged:
            accountEntryData.subAccount &&
            accountEntryData.subAccountOf?.id !==
              accountDataToEdit.subAccountOf?.id,
          subAccountOfValues: {
            new: accountEntryData.subAccountOf?.id,
            old: accountDataToEdit.subAccountOf?.id,
          },
        });

        if (structuralPropsChanged) {
          // Only update hierarchy if structural properties changed
          console.log("Updating hierarchy due to structural changes with atomic transaction");

          // Atomic read-modify-write on the FRESH hierarchy: extract this
          // account's sub-account ids, remove it, and re-insert at its new
          // position — all composed against fresh server data.
          await updateHierarchyTx(db, org, ["accountsHierarchy"], (fresh) => {
            const freshHierarchy = fresh.accountsHierarchy;

            // Grab the subAccount Id's of this account in the hierarchy, if any.
            // Normalize to string ids so legacy `{id}` entries aren't rewritten
            // back into the hierarchy in object form.
            const subAccountIds = freshHierarchy.types
              ?.find((type) => type.type === accountEntryData.type)
              ?.groups.find(
                (group) => group.groupName === accountDataToEdit.group,
              )
              ?.accounts.find(
                (account) => account.id === accountDataToEdit.accountDatabaseId,
              )
              ?.subAccounts?.map(normalizeSubAccountId);

            console.log("SubAccountIds: ", subAccountIds);

            const newRemovedAccountHierarchy = removeHierarchyAccount(
              freshHierarchy,
              accountDataToEdit.accountDatabaseId,
            );

            const finalHierarchy = createNewHierarchy(
              newRemovedAccountHierarchy,
              accountEntryData.type,
              accountEntryData.group,
              accountDataToEdit.accountDatabaseId,
              accountEntryData.subAccount
                ? accountEntryData.subAccountOf.id
                : null,
              subAccountIds,
            );

            const editAccountRef = doc(
              db,
              "orgs",
              org,
              "accounts",
              accountDataToEdit.accountDatabaseId,
            );

            return {
              writes: { accountsHierarchy: finalHierarchy },
              extraWrites: (transaction) =>
                transaction.set(editAccountRef, accountData, { merge: true }),
            };
          });
        } else {
          console.log(
            "No structural changes detected, preserving hierarchy position",
          );
          // No structural changes - hierarchy stays the same, only update account document
          await setAccountDocument(
            accountData,
            accountDataToEdit.accountDatabaseId,
          );
        }

        // Note: Account document is updated in transaction above for structural changes
        // or in the else block above for non-structural changes
      }
      console.log("Returning from createAccount:", accountRef);
      return accountRef;
    };

    if (accountEntryData.accountNumber !== "" && !massAdd) {
      // Reject a duplicate number up front (normalized on both sides), surfacing
      // a reason the form can display instead of an alert + fake success.
      const dupError = findDuplicateNumberError({
        accounts,
        accountNumber: accountEntryData.accountNumber,
        editingAccountId,
      });
      if (dupError) return dupError;

      const result = await createAccount(accountEntryData.accountNumber);
      console.log("Received from createAccount:", result);
      // createAccount returns { error } on a failure it detected (e.g. an
      // attempted type change) — propagate it as the failure contract.
      if (result?.error) return result;
      accountRef = result;
      if (!accountRef) {
        console.log("Account not created");
        return { error: "Failed to save the account. Please try again." };
      }
    } else if (massAdd) {
      const accountsRef = collection(db, "orgs", org, "accounts");
      accountRef = await addDoc(accountsRef, accountData);
      console.log("Added Account: ", accountRef);
      return { [accountNumber]: accountRef.id };
    }
    if (accountRef && accountEntryData.startingBalance > 0) {
      console.log("accountRef before addJournalEntry:", accountRef);
      await addJournalEntry({
        accountEntryData,
        accounts,
        funds,
        accountId: accountRef.id,
        user,
        org,
        db,
        setTransactions,
        newAccountOverride: {
          accountType: accountEntryData.type,
          accountName: accountEntryData.accountName,
        },
      });
    }

    // Close modal and reset data AFTER starting balance is saved
    if (!editingAccountId && accountRef) {
      setShowModal(false);
      // Reset to the FULL blank shape — this replaces state rather than
      // merging, so a partial object leaves the omitted keys undefined and
      // strands the previous account's values in the next form.
      setAccountEntryData({ ...BLANK_ACCOUNT_FORM });
    } else if (editingAccountId) {
      setShowModal(false);
    }

    return accountRef;
  };
  return { handleSubmit };
};

export const useSubmitFund = ({
  fundEntryData,
  editingFundId,
  setShowModal,
  fundDataToEdit,
  setFundEntryData,
}) => {
  const { org, user } = useContext(UserContext);
  const authHook = useAuth();
  const db = authHook.db;

  //Add fund to database
  const handleSubmit = async () => {
    console.log("Submitting Create Fund");

    // Server-side validation: reject blank/invalid fund data
    const trimmedName = fundEntryData.fundName?.trim();
    const trimmedNumber = fundEntryData.fundNumber?.toString().trim();
    const trimmedGroup = fundEntryData.group?.trim();

    if (!trimmedName) {
      throw new Error("Fund name is required.");
    }
    if (!trimmedNumber) {
      throw new Error("Fund number is required.");
    }
    if (!trimmedNumber.startsWith("3")) {
      throw new Error("Fund number must start with 3.");
    }
    if (!trimmedGroup) {
      throw new Error("Fund group is required.");
    }
    if (fundEntryData.subFund && !fundEntryData.subFundOf?.id) {
      throw new Error("Sub-fund must have a parent fund.");
    }

    const fundData = {
      fundName: trimmedName,
      fundNumber: trimmedNumber,
      isRestricted: fundEntryData.isRestricted ? true : false,
      userMadeChange: user.uid,
      // donorReason is the donor-friendly name shown instead of fundName
      ...(fundEntryData.donorReason && { donorReason: fundEntryData.donorReason.trim() }),
      // endDate for temporary sub-funds (auto-close on this date)
      endDate: fundEntryData.endDate ? fundEntryData.endDate.toDate() : null,
    };

    const createFund = async () => {
      // Creating a new fund — atomic transaction for all 4 documents
      if (!editingFundId) {
        console.log("Adding Fund with atomic transaction: ", fundData);

        // Multi-doc atomic RMW: BOTH accountsHierarchy and fundsHierarchy are
        // read fresh, then the fund doc + equity account + both hierarchies are
        // written in one transaction.
        await updateHierarchyTx(
          db,
          org,
          ["accountsHierarchy", "fundsHierarchy"],
          (fresh) => {
            const fundsRef = collection(db, "orgs", org, "funds");
            const newFundRef = doc(fundsRef);
            const fundID = newFundRef.id;

            const parentFundId = fundEntryData.subFund
              ? fundEntryData.subFundOf.id
              : null;

            const newFundHierarchy = createNewFundHierarchy(
              fresh.fundsHierarchy,
              fundEntryData.group,
              fundID,
              parentFundId,
              null,
            );

            const newAccountHierarchy = createNewHierarchy(
              fresh.accountsHierarchy,
              "Equity",
              "Fund-Linked Accounts",
              fundID,
            );

            const accountRef = doc(db, "orgs", org, "accounts", fundID);

            return {
              writes: {
                accountsHierarchy: newAccountHierarchy,
                fundsHierarchy: newFundHierarchy,
              },
              extraWrites: (transaction) => {
                transaction.set(newFundRef, fundData);
                transaction.set(accountRef, {
                  accountName: fundData.fundName,
                  accountNumber: fundData.fundNumber,
                  accountType: "Equity",
                  group: "Fund-Linked Accounts",
                  userMadeChange: user.uid,
                });
              },
            };
          },
        );

        return;
      }

      // Editing an existing fund and its linked equity account
      if (editingFundId) {
        // Merge-write payload built with property-present discipline so that
        // fields the submitting form doesn't manage (e.g. donorReason/endDate)
        // and unrelated stored fields (e.g. disabled) survive the edit.
        const fundEditPayload = buildFundEditPayload(fundEntryData, user);

        // The Equity mirror doc written alongside the fund in both the
        // structural and non-structural paths.
        const equityAccountDoc = {
          accountName: fundEditPayload.fundName,
          accountNumber: fundEditPayload.fundNumber,
          accountType: "Equity",
          group: "Fund-Linked Accounts",
          userMadeChange: user.uid,
        };

        // Check if structural properties changed (properties that affect hierarchy position)
        const structuralPropsChanged =
          fundEntryData.group !== fundDataToEdit.group ||
          fundEntryData.subFund !== fundDataToEdit.subFund ||
          (fundEntryData.subFund &&
            fundEntryData.subFundOf?.id !== fundDataToEdit.subFundOf?.id);

        console.log(
          "Fund structural properties changed:",
          structuralPropsChanged,
        );
        console.log("Fund comparison details:", {
          groupChanged: fundEntryData.group !== fundDataToEdit.group,
          groupValues: { new: fundEntryData.group, old: fundDataToEdit.group },
          subFundChanged: fundEntryData.subFund !== fundDataToEdit.subFund,
          subFundValues: {
            new: fundEntryData.subFund,
            old: fundDataToEdit.subFund,
          },
          subFundOfChanged:
            fundEntryData.subFund &&
            fundEntryData.subFundOf?.id !== fundDataToEdit.subFundOf?.id,
          subFundOfValues: {
            new: fundEntryData.subFundOf?.id,
            old: fundDataToEdit.subFundOf?.id,
          },
        });

        if (structuralPropsChanged) {
          // Structural changes: wrap fund doc + equity account + both hierarchies in transaction
          console.log("Updating fund hierarchy due to structural changes with atomic transaction");

          // Multi-doc atomic RMW: sub-fund extraction + remove/create for BOTH
          // hierarchies composed against fresh data; fund doc + equity account
          // keep their Wave-2 merge semantics.
          await updateHierarchyTx(
            db,
            org,
            ["accountsHierarchy", "fundsHierarchy"],
            (fresh) => {
              const subFundIds = fresh.fundsHierarchy.groups
                ?.find((group) => group.groupName === fundDataToEdit.group)
                ?.funds.find(
                  (fund) => fund.id === fundDataToEdit.fundDatabaseId,
                )
                ?.subFunds;
              console.log("SubFundIds: ", subFundIds);

              const newRemovedFundHierarchy = removeFundHierarchy(
                fresh.fundsHierarchy,
                fundDataToEdit.fundDatabaseId,
              );

              const newRemovedAccountHierarchy = removeHierarchyAccount(
                fresh.accountsHierarchy,
                fundDataToEdit.fundDatabaseId,
              );

              const parentFundId = fundEntryData.subFund
                ? fundEntryData.subFundOf.id
                : null;

              const newFundHierarchy = createNewFundHierarchy(
                newRemovedFundHierarchy,
                fundEntryData.group,
                editingFundId,
                parentFundId,
                subFundIds ? subFundIds : null,
              );

              const newAccountHierarchy = createNewHierarchy(
                newRemovedAccountHierarchy,
                "Equity",
                "Fund-Linked Accounts",
                editingFundId,
              );

              const fundRef = doc(db, "orgs", org, "funds", editingFundId);
              const accountRef = doc(db, "orgs", org, "accounts", editingFundId);

              return {
                writes: {
                  accountsHierarchy: newAccountHierarchy,
                  fundsHierarchy: newFundHierarchy,
                },
                extraWrites: (transaction) => {
                  transaction.set(fundRef, fundEditPayload, { merge: true });
                  transaction.set(accountRef, equityAccountDoc, { merge: true });
                },
              };
            },
          );
        } else {
          console.log(
            "No fund structural changes detected, preserving hierarchy position",
          );
          // No structural changes - just update fund doc + equity account
          const fundRef = doc(db, "orgs", org, "funds", editingFundId);
          const accountRef = doc(db, "orgs", org, "accounts", editingFundId);
          await setDoc(fundRef, fundEditPayload, { merge: true });
          await setDoc(accountRef, equityAccountDoc, { merge: true });
        }

      }
    };

    //If the account number doesn't exist, create the account
    await createFund();
  };
  return { handleSubmit };
};
